import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  EventAvailable as AvailableIcon,
  EventBusy as BusyIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import type { ContractorAvailability, CreateAvailabilityData } from '../services/contractorAvailabilityService';

interface ContractorAvailabilityCalendarProps {
  availability: ContractorAvailability[];
  onAddAvailability: (data: CreateAvailabilityData) => Promise<void>;
  onUpdateAvailability: (id: string, data: Partial<CreateAvailabilityData> & { isActive?: boolean }) => Promise<void>;
  onDeleteAvailability: (id: string) => Promise<void>;
  onMonthChange?: (startDate: Date, endDate: Date) => void;
}

const ContractorAvailabilityCalendar: React.FC<ContractorAvailabilityCalendarProps> = ({
  availability,
  onAddAvailability,
  onUpdateAvailability,
  onDeleteAvailability,
  onMonthChange,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [timeSlotDialogOpen, setTimeSlotDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<ContractorAvailability | null>(null);
  const [slotsForSelectedDate, setSlotsForSelectedDate] = useState<ContractorAvailability[]>([]);
  const [slotForm, setSlotForm] = useState({
    startTime: '',
    endTime: '',
    isBusy: false,
    notes: '',
  });

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];
    
    // Previous month's trailing days
    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
      });
    }
    
    // Current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(currentDate.getFullYear(), currentDate.getMonth(), day),
        isCurrentMonth: true,
      });
    }
    
    // Next month's leading days
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, day),
        isCurrentMonth: false,
      });
    }
    
    return days;
  }, [currentDate, startingDayOfWeek, daysInMonth]);

  // Get availability for a specific date
  const getAvailabilityForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return availability.filter((slot) => {
      const slotStart = new Date(slot.startTime);
      const slotStartStr = slotStart.toISOString().split('T')[0];
      
      // For recurring slots, check recurring pattern
      if (slot.isRecurring && slot.recurringPattern) {
        // Check if date is on or after the slot start date
        if (dateStr < slotStartStr) {
          return false;
        }
        
        // Check if recurring pattern has an endDate and if date is before it
        if (slot.recurringPattern.endDate) {
          const endDate = new Date(slot.recurringPattern.endDate);
          const endDateStr = endDate.toISOString().split('T')[0];
          if (dateStr > endDateStr) {
            return false;
          }
        }
        
        // Check if the day of week matches (for weekly recurring patterns)
        if (slot.recurringPattern.daysOfWeek && slot.recurringPattern.daysOfWeek.length > 0) {
          const dayOfWeek = date.getDay();
          return slot.recurringPattern.daysOfWeek.includes(dayOfWeek);
        }
        
        // For daily or monthly recurring patterns without daysOfWeek, check if date matches pattern
        // For now, if no daysOfWeek specified, show on all days (can be refined based on type/interval)
        return true;
      }
      
      // For one-time slots, check if date falls within slot range
      const slotEnd = new Date(slot.endTime);
      const slotEndStr = slotEnd.toISOString().split('T')[0];
      return slotStartStr <= dateStr && dateStr <= slotEndStr;
    });
  };

  const handleDateClick = (date: Date, isCurrentMonth: boolean) => {
    if (!isCurrentMonth) return; // Don't allow clicking on dates from other months
    
    setSelectedDate(date);
    const slots = getAvailabilityForDate(date);
    setSlotsForSelectedDate(slots);
    
    if (slots.length > 0) {
      // If multiple slots, show first one (user can select which to edit)
      setSelectedSlot(slots[0]);
      const slotStart = new Date(slots[0].startTime);
      const slotEnd = new Date(slots[0].endTime);
      setSlotForm({
        startTime: `${String(slotStart.getHours()).padStart(2, '0')}:${String(slotStart.getMinutes()).padStart(2, '0')}`,
        endTime: `${String(slotEnd.getHours()).padStart(2, '0')}:${String(slotEnd.getMinutes()).padStart(2, '0')}`,
        isBusy: !slots[0].isActive,
        notes: slots[0].notes || '',
      });
    } else {
      // New slot
      setSelectedSlot(null);
      setSlotForm({
        startTime: '09:00',
        endTime: '17:00',
        isBusy: false,
        notes: '',
      });
    }
    setTimeSlotDialogOpen(true);
  };

  const handleSaveSlot = async () => {
    if (!selectedDate) return;

    const dateStr = selectedDate.toISOString().split('T')[0];
    const startDateTime = `${dateStr}T${slotForm.startTime}:00`;
    const endDateTime = `${dateStr}T${slotForm.endTime}:00`;

    if (selectedSlot) {
      // Update existing slot
      await onUpdateAvailability(selectedSlot._id, {
        startTime: new Date(startDateTime).toISOString(),
        endTime: new Date(endDateTime).toISOString(),
        isActive: !slotForm.isBusy,
        notes: slotForm.notes || undefined,
      });
    } else {
      // Create new slot
      await onAddAvailability({
        startTime: new Date(startDateTime).toISOString(),
        endTime: new Date(endDateTime).toISOString(),
        isActive: !slotForm.isBusy,
        notes: slotForm.notes || undefined,
      });
    }

    setTimeSlotDialogOpen(false);
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const handleDeleteSlot = async () => {
    if (selectedSlot) {
      await onDeleteAvailability(selectedSlot._id);
      setTimeSlotDialogOpen(false);
      setSelectedDate(null);
      setSelectedSlot(null);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      
      // Notify parent when user explicitly navigates months
      if (onMonthChange) {
        const firstDay = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
        const lastDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
        onMonthChange(firstDay, lastDay);
      }
      
      return newDate;
    });
  };

  // REMOVED: Automatic month change notification on currentDate change
  // This was causing infinite loops. We now only notify when user explicitly changes months
  // via navigateMonth() or the "Today" button, which already call onMonthChange directly.

  const getDayStatus = (date: Date) => {
    const slots = getAvailabilityForDate(date);
    if (slots.length === 0) return null;
    
    const availableSlots = slots.filter(s => s.isActive);
    const busySlots = slots.filter(s => !s.isActive);
    
    return {
      available: availableSlots.length,
      busy: busySlots.length,
      total: slots.length,
      slots: slots, // Include full slot data for display
    };
  };

  return (
    <Box>
      {/* Calendar Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigateMonth('prev')}>
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {currentDate.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const today = new Date();
              // Notify parent when user explicitly clicks Today
              if (onMonthChange) {
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                onMonthChange(firstDay, lastDay);
              }
              
              setCurrentDate(today);
            }}
          >
            Today
          </Button>
        </Box>
        <IconButton onClick={() => navigateMonth('next')}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#4caf50', borderRadius: 1 }} />
          <Typography variant="caption">Available</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#f44336', borderRadius: 1 }} />
          <Typography variant="caption">Busy</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#e0e0e0', borderRadius: 1 }} />
          <Typography variant="caption">No availability set</Typography>
        </Box>
      </Box>

      {/* Calendar Grid */}
      <Paper variant="outlined">
        <Grid container>
          {/* Day headers */}
          {daysOfWeek.map((day) => (
            <Grid item xs={12 / 7} key={day} sx={{ p: 1, textAlign: 'center', bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="caption" fontWeight="bold">{day}</Typography>
            </Grid>
          ))}
          
          {/* Calendar days */}
          {calendarDays.map((day, index) => {
            const status = getDayStatus(day.date);
            const isToday = day.date.toDateString() === new Date().toDateString();
            
            return (
              <Grid
                item
                xs={12 / 7}
                key={index}
                sx={{
                  minHeight: 100,
                  border: '1px solid #e0e0e0',
                  borderTop: 'none',
                  borderLeft: index % 7 === 0 ? '1px solid #e0e0e0' : 'none',
                  bgcolor: day.isCurrentMonth ? 'white' : '#f9f9f9',
                  cursor: day.isCurrentMonth ? 'pointer' : 'default',
                  '&:hover': day.isCurrentMonth ? {
                    bgcolor: '#f5f5f5',
                  } : {},
                  position: 'relative',
                }}
                onClick={() => day.isCurrentMonth && handleDateClick(day.date, day.isCurrentMonth)}
              >
                <Box sx={{ p: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isToday ? 'bold' : 'normal',
                      color: day.isCurrentMonth ? 'text.primary' : 'text.secondary',
                      mb: 0.5,
                    }}
                  >
                    {day.date.getDate()}
                  </Typography>
                  
                  {status && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                      {status.slots.slice(0, 3).map((slot: ContractorAvailability, idx: number) => {
                        const start = new Date(slot.startTime);
                        const end = new Date(slot.endTime);
                        const isAvailable = slot.isActive;
                        const timeStr = `${start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}-${end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
                        
                        return (
                          <Tooltip 
                            key={idx} 
                            title={`${isAvailable ? 'Available' : 'Busy'}: ${timeStr}${slot.notes ? ` - ${slot.notes}` : ''}`}
                          >
                            <Box
                              sx={{
                                bgcolor: isAvailable ? '#4caf50' : '#f44336',
                                color: 'white',
                                borderRadius: 0.5,
                                px: 0.5,
                                py: 0.25,
                                fontSize: '0.65rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.25,
                                cursor: 'pointer',
                              }}
                            >
                              {isAvailable ? (
                                <AvailableIcon sx={{ fontSize: 10 }} />
                              ) : (
                                <BusyIcon sx={{ fontSize: 10 }} />
                              )}
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {timeStr}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                      {status.total > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: 0.25 }}>
                          +{status.total - 3} more
                        </Typography>
                      )}
                    </Box>
                  )}
                  {!status && day.isCurrentMonth && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.5 }}>
                      Click to add
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Time Slot Dialog */}
      <Dialog 
        open={timeSlotDialogOpen} 
        onClose={() => {
          setTimeSlotDialogOpen(false);
          // Keep selectedDate and slotsForSelectedDate so calendar state persists
          // Only clear selectedSlot to allow adding new slots on the same date
          setSelectedSlot(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {selectedSlot ? 'Edit Availability Slot' : 'Add Availability Slot'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Date: {selectedDate?.toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
            
            {slotsForSelectedDate.length > 1 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Existing Slots on This Day ({slotsForSelectedDate.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 200, overflowY: 'auto', p: 1, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                  {slotsForSelectedDate.map((slot, idx) => {
                    const start = new Date(slot.startTime);
                    const end = new Date(slot.endTime);
                    const isSelected = selectedSlot?._id === slot._id;
                    return (
                      <Box
                        key={slot._id}
                        onClick={() => {
                          setSelectedSlot(slot);
                          setSlotForm({
                            startTime: `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
                            endTime: `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`,
                            isBusy: !slot.isActive,
                            notes: slot.notes || '',
                          });
                        }}
                        sx={{
                          p: 1,
                          border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                          borderRadius: 1,
                          bgcolor: isSelected ? '#e3f2fd' : 'white',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'}>
                            {start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - {end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                          <Chip
                            label={slot.isActive ? 'Available' : 'Busy'}
                            color={slot.isActive ? 'success' : 'error'}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        {isSelected && <CheckCircleIcon color="primary" fontSize="small" />}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Time"
                type="time"
                value={slotForm.startTime}
                onChange={(e) => setSlotForm({ ...slotForm, startTime: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="End Time"
                type="time"
                value={slotForm.endTime}
                onChange={(e) => setSlotForm({ ...slotForm, endTime: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={slotForm.isBusy}
                  onChange={(e) => setSlotForm({ ...slotForm, isBusy: e.target.checked })}
                />
              }
              label="Mark as Busy (Unavailable)"
            />
            
            <TextField
              label="Notes (Optional)"
              value={slotForm.notes}
              onChange={(e) => setSlotForm({ ...slotForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {selectedSlot && (
            <Button onClick={handleDeleteSlot} color="error">
              Delete
            </Button>
          )}
          {slotsForSelectedDate.length > 0 && (
            <Button
              onClick={() => {
                setSelectedSlot(null);
                setSlotForm({
                  startTime: '09:00',
                  endTime: '17:00',
                  isBusy: false,
                  notes: '',
                });
              }}
              variant="outlined"
            >
              Add New Slot
            </Button>
          )}
          <Button onClick={() => {
            setTimeSlotDialogOpen(false);
            // Keep selectedDate and slotsForSelectedDate so calendar state persists
            setSelectedSlot(null);
          }}>Cancel</Button>
          <Button onClick={handleSaveSlot} variant="contained">
            {selectedSlot ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractorAvailabilityCalendar;

